/**
 * NotificationSettings Page
 *
 * Settings page for configuring notification preferences including:
 * - Notification channels (push, email, SMS)
 * - Frequency settings
 * - Quiet hours
 * - Digest options
 *
 * Based on SETTINGS_FUNCTIONAL_SPEC.md
 */

import { useState } from 'react';
import { SettingsLayout } from '../../components/settings/SettingsLayout';
import {
  Bell,
  Mail,
  Smartphone,
  MessageSquare,
  Clock,
  Calendar,
  Check,
  Loader2,
} from 'lucide-react';

interface NotificationChannel {
  id: 'push' | 'email' | 'sms';
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface QuietHours {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface DigestSettings {
  enabled: boolean;
  frequency: 'daily' | 'weekly';
  dayOfWeek?: number; // 0 = Sunday, 6 = Saturday (for weekly)
  timeOfDay: string;
}

type NotificationFrequency = 'immediate' | 'hourly' | 'daily';

export function NotificationSettings(): JSX.Element {
  // Channel settings
  const [channels, setChannels] = useState<NotificationChannel[]>([
    {
      id: 'push',
      label: 'Push Notifications',
      description: 'Receive notifications on your device',
      icon: <Bell className="w-5 h-5" />,
      enabled: true,
    },
    {
      id: 'email',
      label: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: <Mail className="w-5 h-5" />,
      enabled: true,
    },
    {
      id: 'sms',
      label: 'SMS Notifications',
      description: 'Receive text message alerts for urgent items',
      icon: <MessageSquare className="w-5 h-5" />,
      enabled: false,
    },
  ]);

  // Notification types and their frequency
  const [deadlineFrequency, setDeadlineFrequency] = useState<NotificationFrequency>('immediate');
  const [careLogFrequency, setCareLogFrequency] = useState<NotificationFrequency>('daily');
  const [teamUpdateFrequency, setTeamUpdateFrequency] = useState<NotificationFrequency>('immediate');

  // Quiet hours
  const [quietHours, setQuietHours] = useState<QuietHours>({
    enabled: false,
    startTime: '22:00',
    endTime: '07:00',
  });

  // Digest settings
  const [digest, setDigest] = useState<DigestSettings>({
    enabled: true,
    frequency: 'daily',
    dayOfWeek: 1, // Monday
    timeOfDay: '09:00',
  });

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleChannelToggle = (channelId: string) => {
    setChannels((prev) =>
      prev.map((ch) =>
        ch.id === channelId ? { ...ch, enabled: !ch.enabled } : ch
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Placeholder for saving to backend
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const frequencyOptions: { value: NotificationFrequency; label: string }[] = [
    { value: 'immediate', label: 'Immediately' },
    { value: 'hourly', label: 'Hourly digest' },
    { value: 'daily', label: 'Daily digest' },
  ];

  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return (
    <SettingsLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-[28px] font-bold text-ink">Notifications</h2>
        <p className="text-muted text-[15px]">Configure how and when you receive notifications</p>
      </div>

      {/* Notification Channels Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <Smartphone className="w-5 h-5 text-evergreen" />
            <h3 className="text-lg font-bold text-ink">Notification Channels</h3>
          </div>
          <p className="text-sm text-muted mb-5">
            Choose how you want to receive notifications
          </p>

          <div className="space-y-3">
            {channels.map((channel) => (
              <div
                key={channel.id}
                className="flex items-center justify-between p-4 bg-airy rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-soft-neutral rounded-lg flex items-center justify-center text-muted">
                    {channel.icon}
                  </div>
                  <div>
                    <p id={`notif-channel-${channel.id}-label`} className="font-semibold text-ink">{channel.label}</p>
                    <p className="text-sm text-muted">{channel.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={channel.enabled}
                  aria-labelledby={`notif-channel-${channel.id}-label`}
                  onClick={() => handleChannelToggle(channel.id)}
                  className={`relative inline-flex h-7 w-12 cursor-pointer rounded-full transition-colors ${
                    channel.enabled ? 'bg-evergreen' : 'bg-soft-neutral'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                      channel.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notification Frequency Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <Clock className="w-5 h-5 text-evergreen" />
            <h3 className="text-lg font-bold text-ink">Notification Frequency</h3>
          </div>
          <p className="text-sm text-muted mb-5">
            Control how often you receive different types of notifications
          </p>

          <div className="space-y-4">
            {/* Deadline Alerts */}
            <div className="flex items-center justify-between py-3 border-b border-soft-neutral">
              <div>
                <p id="notif-deadline-label" className="font-medium text-ink">Deadline Alerts</p>
                <p className="text-sm text-muted">Upcoming deadlines and reminders</p>
              </div>
              <select
                aria-labelledby="notif-deadline-label"
                value={deadlineFrequency}
                onChange={(e) => setDeadlineFrequency(e.target.value as NotificationFrequency)}
                className="input w-40"
              >
                {frequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Care Log Reminders */}
            <div className="flex items-center justify-between py-3 border-b border-soft-neutral">
              <div>
                <p id="notif-carelog-label" className="font-medium text-ink">Care Log Reminders</p>
                <p className="text-sm text-muted">Reminders to log daily care activities</p>
              </div>
              <select
                aria-labelledby="notif-carelog-label"
                value={careLogFrequency}
                onChange={(e) => setCareLogFrequency(e.target.value as NotificationFrequency)}
                className="input w-40"
              >
                {frequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Care Team Updates */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p id="notif-team-label" className="font-medium text-ink">Care Team Updates</p>
                <p className="text-sm text-muted">When team members add or update information</p>
              </div>
              <select
                aria-labelledby="notif-team-label"
                value={teamUpdateFrequency}
                onChange={(e) => setTeamUpdateFrequency(e.target.value as NotificationFrequency)}
                className="input w-40"
              >
                {frequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Quiet Hours Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-evergreen" />
              <h3 className="text-lg font-bold text-ink">Quiet Hours</h3>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={quietHours.enabled}
              aria-label="Quiet Hours"
              onClick={() => setQuietHours((prev) => ({ ...prev, enabled: !prev.enabled }))}
              className={`relative inline-flex h-7 w-12 cursor-pointer rounded-full transition-colors ${
                quietHours.enabled ? 'bg-evergreen' : 'bg-soft-neutral'
              }`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                  quietHours.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-muted mb-5">
            Pause non-urgent notifications during specified hours
          </p>

          {quietHours.enabled && (
            <div className="flex items-center gap-4 p-4 bg-airy rounded-lg">
              <div className="flex-1">
                <label htmlFor="notif-quiet-start" className="label">Start Time</label>
                <input
                  id="notif-quiet-start"
                  type="time"
                  value={quietHours.startTime}
                  onChange={(e) =>
                    setQuietHours((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                  className="input"
                />
              </div>
              <div className="pt-6 text-muted">to</div>
              <div className="flex-1">
                <label htmlFor="notif-quiet-end" className="label">End Time</label>
                <input
                  id="notif-quiet-end"
                  type="time"
                  value={quietHours.endTime}
                  onChange={(e) =>
                    setQuietHours((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                  className="input"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Digest Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-evergreen" />
              <h3 className="text-lg font-bold text-ink">Email Digest</h3>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={digest.enabled}
              aria-label="Email Digest"
              onClick={() => setDigest((prev) => ({ ...prev, enabled: !prev.enabled }))}
              className={`relative inline-flex h-7 w-12 cursor-pointer rounded-full transition-colors ${
                digest.enabled ? 'bg-evergreen' : 'bg-soft-neutral'
              }`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                  digest.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-muted mb-5">
            Receive a summary of activity and upcoming deadlines
          </p>

          {digest.enabled && (
            <div className="space-y-4 p-4 bg-airy rounded-lg">
              <div>
                <label className="label">Frequency</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDigest((prev) => ({ ...prev, frequency: 'daily' }))}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      digest.frequency === 'daily'
                        ? 'bg-evergreen text-white'
                        : 'bg-white border border-soft-neutral text-ink hover:border-evergreen'
                    }`}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setDigest((prev) => ({ ...prev, frequency: 'weekly' }))}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      digest.frequency === 'weekly'
                        ? 'bg-evergreen text-white'
                        : 'bg-white border border-soft-neutral text-ink hover:border-evergreen'
                    }`}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              {digest.frequency === 'weekly' && (
                <div>
                  <label htmlFor="notif-digest-day" className="label">Day of Week</label>
                  <select
                    id="notif-digest-day"
                    value={digest.dayOfWeek}
                    onChange={(e) =>
                      setDigest((prev) => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))
                    }
                    className="input"
                  >
                    {daysOfWeek.map((day, index) => (
                      <option key={day} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="notif-digest-time" className="label">Time of Day</label>
                <input
                  id="notif-digest-time"
                  type="time"
                  value={digest.timeOfDay}
                  onChange={(e) => setDigest((prev) => ({ ...prev, timeOfDay: e.target.value }))}
                  className="input"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        {saveSuccess && (
          <span className="flex items-center gap-2 text-evergreen text-sm font-medium">
            <Check className="w-4 h-4" />
            Settings saved successfully
          </span>
        )}
      </div>
    </SettingsLayout>
  );
}

export default NotificationSettings;
